import { useCallback, useEffect, useState } from "react";
import { and, eq, inArray } from "drizzle-orm";
import { db } from "../db";
import * as s from "../../../shared/src/schema";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { useColors, useStyles } from "../ui";
import {
  useCommand,
  useListVersion,
  useRpc,
  useSyncStatus,
} from "../store";
import { uuidv7 } from "../../../shared/src/ids";
import type {
  MessageAudienceType,
  MessageSend,
} from "../../../shared/src/commands";
import type { MessageItem, MessageListResult } from "../../../shared/src/protocol";

function useFormatRelative() {
  const { t } = useI18n();
  return (ts: number): string => {
    const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (diffSec < 60) return t("messages.relativeJustNow");
    if (diffSec < 3600)
      return t("messages.relativeMinutesAgo", { n: String(Math.floor(diffSec / 60)) });
    if (diffSec < 86400)
      return t("messages.relativeHoursAgo", { n: String(Math.floor(diffSec / 3600)) });
    return new Date(ts).toLocaleString();
  };
}

export function MessagesPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const { online } = useSyncStatus();
  const colors = useColors();
  const styles = useStyles();
  const rpc = useRpc();
  const dispatch = useCommand();
  const listVersion = useListVersion();
  const formatRelative = useFormatRelative();

  const isAdmin = user?.role === "admin";
  const canBroadcastHospital = isAdmin || !!user?.canBroadcastToOwnHospital;
  const canBroadcastUnit = isAdmin || !!user?.canBroadcastToOwnUnit;
  const canSendAnything = isAdmin || canBroadcastHospital || canBroadcastUnit;

  // ── Inbox ────────────────────────────────────────────────────────────
  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [inboxLoading, setInboxLoading] = useState(true);

  useEffect(() => {
    if (!online) return;
    let cancelled = false;
    setInboxLoading(true);
    rpc<MessageListResult>({ type: "listMyMessages", limit: 50 })
      .then((res) => {
        if (cancelled) return;
        setMessages(res.messages);
        setHasMore(res.hasMore);
      })
      .catch((e) => console.warn("[Messages] failed to load inbox", e))
      .finally(() => {
        if (!cancelled) setInboxLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rpc, online, listVersion]);

  const loadOlder = useCallback(async () => {
    if (messages.length === 0) return;
    const beforeTs = messages[messages.length - 1].createdAt;
    try {
      const res = await rpc<MessageListResult>({
        type: "listMyMessages",
        beforeTs,
        limit: 50,
      });
      setMessages((prev) => [...prev, ...res.messages]);
      setHasMore(res.hasMore);
    } catch (e) {
      console.warn("[Messages] failed to load older", e);
    }
  }, [rpc, messages]);

  // ── Compose: hospital / unit options for the current user ───────────
  const [hospitalOptions, setHospitalOptions] = useState<
    Array<{ id: string; name: string | null }>
  >([]);
  const [unitOptions, setUnitOptions] = useState<
    Array<{ id: string; name: string | null }>
  >([]);

  useEffect(() => {
    let cancelled = false;
    const userHospitalIds = user?.hospitalIds ?? [];
    const userUnitIds = user?.unitIds ?? [];
    (async () => {
      const hospitalQuery = isAdmin
        ? db
            .select({ id: s.hospitals.id, name: s.hospitals.name })
            .from(s.hospitals)
            .where(eq(s.hospitals.archived, 0))
        : userHospitalIds.length === 0
          ? Promise.resolve([])
          : db
              .select({ id: s.hospitals.id, name: s.hospitals.name })
              .from(s.hospitals)
              .where(
                and(
                  eq(s.hospitals.archived, 0),
                  inArray(s.hospitals.id, userHospitalIds),
                ),
              );
      const unitQuery = isAdmin
        ? db
            .select({ id: s.units.id, name: s.units.name })
            .from(s.units)
            .where(eq(s.units.archived, 0))
        : userUnitIds.length === 0
          ? Promise.resolve([])
          : db
              .select({ id: s.units.id, name: s.units.name })
              .from(s.units)
              .where(
                and(
                  eq(s.units.archived, 0),
                  inArray(s.units.id, userUnitIds),
                ),
              );
      const [hRows, uRows] = await Promise.all([hospitalQuery, unitQuery]);
      if (cancelled) return;
      setHospitalOptions(hRows);
      setUnitOptions(uRows);
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, user?.hospitalIds, user?.unitIds, listVersion]);

  // ── Compose form state ──────────────────────────────────────────────
  const [audience, setAudience] = useState<MessageAudienceType>(
    isAdmin ? "everyone" : canBroadcastHospital ? "hospital" : "unit",
  );
  const [hospitalId, setHospitalId] = useState<string>("");
  const [unitId, setUnitId] = useState<string>("");
  const [includeSubordinates, setIncludeSubordinates] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sentToast, setSentToast] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSendError(null);
    setSending(true);
    try {
      const payload: MessageSend = {
        uuid: uuidv7(),
        audienceType: audience,
        body: body.trim(),
      };
      if (audience === "hospital") {
        if (!hospitalId) {
          setSendError(t("messages.pickHospitalError"));
          setSending(false);
          return;
        }
        payload.audienceHospitalId = hospitalId;
      } else if (audience === "unit") {
        if (!unitId) {
          setSendError(t("messages.pickUnitError"));
          setSending(false);
          return;
        }
        payload.audienceUnitId = unitId;
        payload.includeSubordinateUnits = includeSubordinates;
      }
      const { ack } = await dispatch("message.send", payload);
      await ack;
      setBody("");
      setSentToast(true);
      setTimeout(() => setSentToast(false), 2500);
    } catch (e) {
      setSendError(e instanceof Error ? e.message : t("messages.sendFailed"));
    } finally {
      setSending(false);
    }
  };

  // ── Rendering helpers ───────────────────────────────────────────────
  const audienceLabel = (m: MessageItem): string => {
    if (m.audienceType === "everyone") return t("messages.audienceEveryone");
    if (m.audienceType === "hospital") {
      return t("messages.audienceLabelHospital", {
        name: m.audienceHospitalName ?? t("messages.unknownHospital"),
      });
    }
    if (m.audienceType === "unit") {
      const count = m.audienceUnitIds?.length ?? 0;
      return count <= 1
        ? t("messages.audienceLabelUnit")
        : t("messages.audienceLabelUnitCount", { count: String(count) });
    }
    return m.audienceType;
  };

  return (
    <div style={{ padding: "12px", display: "flex", flexDirection: "column", gap: "16px", height: "100%", overflow: "auto" }}>
      <div>
        <h2 style={{ margin: "0 0 4px", color: colors.text }}>{t("messages.title")}</h2>
        <p style={{ margin: 0, color: colors.textMuted, fontSize: "0.85rem" }}>
          {t("messages.subtitle")}
        </p>
      </div>

      {/* Compose */}
      {canSendAnything && (
        <form
          onSubmit={submit}
          style={{
            border: `1px solid ${colors.border}`,
            borderRadius: "8px",
            padding: "12px",
            background: colors.bgAlt,
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <div style={{ fontWeight: 600, color: colors.text }}>{t("messages.compose")}</div>

          <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
            {isAdmin && (
              <label style={{ display: "flex", alignItems: "center", gap: "4px", color: colors.text }}>
                <input
                  type="radio"
                  name="audience"
                  checked={audience === "everyone"}
                  onChange={() => setAudience("everyone")}
                />
                {t("messages.audienceEveryone")}
              </label>
            )}
            {canBroadcastHospital && (
              <label style={{ display: "flex", alignItems: "center", gap: "4px", color: colors.text }}>
                <input
                  type="radio"
                  name="audience"
                  checked={audience === "hospital"}
                  onChange={() => setAudience("hospital")}
                />
                {t("messages.audienceHospital")}
              </label>
            )}
            {canBroadcastUnit && (
              <label style={{ display: "flex", alignItems: "center", gap: "4px", color: colors.text }}>
                <input
                  type="radio"
                  name="audience"
                  checked={audience === "unit"}
                  onChange={() => setAudience("unit")}
                />
                {t("messages.audienceUnit")}
              </label>
            )}
          </div>

          {audience === "hospital" && (
            <select
              style={{ ...styles.select, maxWidth: "320px" }}
              value={hospitalId}
              onChange={(e) => setHospitalId(e.target.value)}
            >
              <option value="">{t("messages.pickHospital")}</option>
              {hospitalOptions.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name || h.id}
                </option>
              ))}
            </select>
          )}

          {audience === "unit" && (
            <>
              <select
                style={{ ...styles.select, maxWidth: "320px" }}
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
              >
                <option value="">{t("messages.pickUnit")}</option>
                {unitOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.id}
                  </option>
                ))}
              </select>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", color: colors.text }}>
                <input
                  type="checkbox"
                  checked={includeSubordinates}
                  onChange={(e) => setIncludeSubordinates(e.target.checked)}
                />
                {t("messages.includeSubordinates")}
              </label>
            </>
          )}

          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t("messages.bodyPlaceholder")}
            rows={4}
            style={{
              ...styles.input,
              fontFamily: "inherit",
              resize: "vertical",
              minHeight: "80px",
            }}
          />

          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <button
              type="submit"
              disabled={sending || !body.trim() || !online}
              style={{ ...styles.btnPrimary, opacity: sending ? 0.6 : 1 }}
            >
              {sending ? t("messages.sending") : t("messages.send")}
            </button>
            {sendError && (
              <span style={{ color: colors.danger, fontSize: "0.85rem" }}>
                {sendError}
              </span>
            )}
            {sentToast && (
              <span style={{ color: colors.success, fontSize: "0.85rem" }}>
                {t("messages.sent")}
              </span>
            )}
          </div>
        </form>
      )}

      {/* Inbox */}
      <div>
        <h3 style={{ margin: "0 0 8px", color: colors.text }}>{t("messages.inbox")}</h3>
        {inboxLoading && messages.length === 0 ? (
          <p style={{ color: colors.textMuted }}>{t("app.loading")}</p>
        ) : messages.length === 0 ? (
          <p style={{ color: colors.textMuted }}>{t("messages.noMessages")}</p>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "8px" }}>
            {messages.map((m) => (
              <li
                key={m.id}
                style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: "6px",
                  padding: "10px 12px",
                  background: colors.bg,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "12px",
                    fontSize: "0.78rem",
                    color: colors.textMuted,
                    marginBottom: "4px",
                  }}
                >
                  <span>
                    <strong style={{ color: colors.text }}>
                      {m.senderName || m.senderEmail || t("messages.unknownSender")}
                    </strong>
                    {" · "}
                    {audienceLabel(m)}
                  </span>
                  <span title={new Date(m.createdAt).toLocaleString()}>
                    {formatRelative(m.createdAt)}
                  </span>
                </div>
                <div style={{ whiteSpace: "pre-wrap", color: colors.text, fontSize: "0.95rem", lineHeight: 1.4 }}>
                  {m.body}
                </div>
              </li>
            ))}
          </ul>
        )}
        {hasMore && (
          <div style={{ marginTop: "8px" }}>
            <button onClick={loadOlder} style={styles.btn}>
              {t("messages.loadOlder")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
